
// RightsReadWrite enum type
export const RightsReadWrite = {
  ADMIN: "ADMIN",
  WRITE: "WRITE",
  READ: "READ",
  LIMITED: "LIMITED",
  NONE: "NONE",
} as const
export type RightsReadWrite = typeof RightsReadWrite[keyof typeof RightsReadWrite]

// Utility methods for comparing RightsReadWrite states
// Also accepts legacy boolean / "true"|"false" values still stored on older profiles.
export const RightsReadWriteUtils = {
  isAdmin: (rights: 'true'|'false'|boolean| RightsReadWrite) => rights === RightsReadWrite.ADMIN || rights === 'true' || rights === true,
  canWrite: (rights: 'true'|'false'|boolean|RightsReadWrite) => rights === RightsReadWrite.WRITE || rights === RightsReadWrite.ADMIN || rights === 'true' || rights === true,
  canRead: (rights: 'true'|'false'|boolean|RightsReadWrite) => rights === RightsReadWrite.READ || rights === RightsReadWrite.ADMIN || rights === RightsReadWrite.WRITE || rights === 'true' || rights === true,
  isLimited: (rights: RightsReadWrite) => rights === RightsReadWrite.LIMITED,
  isNoneOrUndefined: (rights: 'true'|'false'|boolean|RightsReadWrite | undefined) => rights?.toString() === RightsReadWrite.NONE.toString() || rights === undefined || rights === 'false' || rights === false
};

export type HRModule = {
  moduleId: 'HR'
  accessProfile: RightsReadWrite
  accessSalary: RightsReadWrite
  accessSkill: RightsReadWrite
  /** Rating one's own skills, whatever accessSkill (default true) */
  canEditOwnSkills?: boolean
  accessDocument: RightsReadWrite
  accessJobPath: RightsReadWrite
  accessTrainingOnProfile: boolean
  resourceOverviewCompanyZone: string[]
  wageOverviewOfficeZone: string[]
  wageOverviewCompanyZone: string[]
  resourceOverviewOfficeZoneIds: any[]
  resourceOverviewCompanyZoneIds: any[]
  wageOverviewOfficeZoneIds: any[]
  wageOverviewCompanyZoneIds: any[]
  onboardingAccess: boolean
  signatureCampaignAccess: boolean
  alumniAccess: boolean
  statsAccess: boolean
  headcountAccess: boolean
  headcountValidationAccess: boolean
  settingsAccess: boolean
  settingsDepartureCategoryAccess: boolean
  settingsDegreeFieldCategoryAccess: boolean
  settingsEmployeeVariablesAccess: boolean
  settingsStatusAccess: boolean
  settingsContractSuperTypesAccess: boolean
  settingsContractTypesAccess: boolean
  settingsOnboardingProcessAccess: boolean
  settingsMailTemplatesAccess: boolean
  settingsFirmNewsAccess: boolean
  settingsSignatureTemplatesAccess: boolean
  settingsResourceCreationFieldsAccess: boolean
  resourceOverview: string
  hidePersonnalData: boolean
  canSetSkill: boolean
  canEditProfile: boolean
  resourceProfileAccess: boolean
  wageAccess: boolean
  wageOverview: string
  isAccess: boolean
  resourceOverviewOfficeZone: any[]
  createResourceAccess: boolean
  canSeeOthersProjects: boolean
  canDeleteResource: boolean
  canGrantAdminAccess: boolean
}

export type DashboardModule = {
  moduleId: 'DASHBOARD'
  isAccess: boolean
  productionAccess: boolean
  salesAccess: boolean
  cashFlowAccess: boolean
  resourceAccess: boolean
  salesTargetAccess: boolean
}

export type ProjectModule = {
  canCreateClientProject: boolean;
  moduleId: 'PROJECT'
  projectOverviewOfficeZoneIds: any[]
  projectOverviewCompanyZoneIds: any[]
  isAccess: boolean
  createAccess: boolean
  projectAdvancementAccess: boolean
  planningResourceAccess: RightsReadWrite
  planningResourceScopeAll: boolean
  statsAccess: boolean
  settingsAccess: boolean
  settingsProjectStatusAccess: boolean
  settingsEngagementCategoryAccess: boolean
  settingsCommissionCategoryAccess: boolean
  settingsPricingCategoryAccess: boolean
  settingsObjectItemCategoryAccess: boolean
  projectOverview: string
  overviewOnlyPublic: boolean
  onGlobalAccess: {
    editProject: boolean
    timesheet: boolean
    monitoring: boolean
    budget: boolean
    staffing: boolean
    invoice: boolean
  }
  onDirectorProject: {
    isEnabled: boolean
    editProject: boolean
    timesheet: boolean
    monitoring: boolean
    budget: boolean
    staffing: boolean
    invoice: boolean
  }
  onManagerProject: {
    isEnabled: boolean
    editProject: boolean
    timesheet: boolean
    monitoring: boolean
    budget: boolean
    staffing: boolean
    invoice: boolean
  }
  onContributorProject: {
    isEnabled: boolean
    editProject: boolean
    timesheet: boolean
    monitoring: boolean
    budget: boolean
    staffing: boolean
    invoice: boolean
  }
}

export type TimesheetModule = {
  moduleId: 'TIMESHEET'
  isAccess: boolean
  showAll: boolean
  settingsAccess: boolean
  settingsActivitiesAccess: boolean
  settingsDailyDetailsAccess: boolean
}

export type GlobalSettings = {
  userAccess: boolean
  profileAccess: boolean
  canGrantSuperAdminAccess: boolean
  sessionAccess: boolean
  personalisationAccess: boolean
  consumptionAccess: boolean
  openReplayAccess: boolean
  integrationAccess: boolean
  dataJobAccess: boolean
  dataClientAccess: boolean
  dataDegreeAccess: boolean
  dataSkillAccess: boolean
  dataOfficeAccess: boolean
  dataDocumentTrackingAccess: boolean
  /** Edits the document structure from the document tracking page (read-only without it) */
  dataDocumentStructureAccess: boolean
  dataRoomAccess: boolean
  dataResourceAccess: boolean
  dataInvoicingInformationAccess: boolean
  dataTeamAccess: boolean
  dataUserGroupAccess: boolean
}

export type LeaveModule = {
  accountsAccess: any;
  moduleId: 'LEAVE'
  isAccess: boolean
  showAll: boolean
  calendarAccess: boolean
  requestAccess: boolean
  selfRequestAccess: boolean
  editAllLeaves: boolean
  canSelfValidate: boolean
  canChooseValidator: boolean
  newRequestAccess: boolean
  leaveValidator: boolean
  bulkCreateAccess: boolean
  canCreateForOther: boolean
  canUpdateLeave: boolean
  canDeleteLeave: boolean
  settingsAccess: boolean
  settingsLeaveTypesAccess: boolean
  settingsFormulasAccess: boolean
  settingsPublicHolidaysAccess: boolean
  settingsInitialBalanceAccess: boolean
}

export type EvaluationModule = {
  canCreateCampaign: boolean;
  moduleId: 'EVALUATION'
  evaluationOverviewOfficeZoneIds: any[]
  isAccess: boolean
  showAll: boolean
  evaluationOverview: string
  create360: boolean
  createPeriod: boolean
  createProjectSimplified: boolean
  createProject: boolean
  canEditEvaluation: boolean
  accessCampaign: boolean
  accessSummaries: boolean
  canCreateSummary: boolean
  settingsAccess: boolean
  settingsEvaluationFormsAccess: boolean
  settingsEvaluationAveragesAccess: boolean
  settingsEvaluationOfficesAccess: boolean
}

export type RecruitmentModule = {
  recruitmentOverviewOfficeZoneIds: any[]
  moduleId: 'RECRUITMENT'
  isAccess: boolean
  accessDocument: RightsReadWrite
  screeningAccess: boolean
  recruitmentOverview: string
  canGrantAdminAccess: boolean
  pipegAccess: boolean
  jobBoardAccess: boolean
  candidateBaseAccess: boolean
  candidateHistoryAccess: boolean
  canEditNotManaged: boolean
  jobBoardAdminAccess: boolean
  canDeleteInterview: boolean
  statsAccess: boolean
  canDecideInsteadOfRecruiterEvaluation: boolean
  canReopenEvaluation: boolean
  canDecideProcessResult: boolean
  canDecideProcess: boolean
  canReopenProcess: boolean
  canDownloadInterviewCandidateFile: boolean
  displayOnlyMyMeetingInCalendar: boolean
  myMeetingsAccess: boolean
  recruitmentCalendarAccess: boolean
  recruitmentOverviewOfficeZone: { _id: string; display: string }[]
  settingsAcess: boolean
  settingsEvaluationsAccess: boolean
  settingsWebsitesAccess: boolean
  settingsMailTemplatesAccess: boolean
  settingsOfficesAccess: boolean
  settingsRejectionAccess: boolean
  settingsWithdrawalAccess: boolean
  settingsCustomQuestionsAccess: boolean
  settingsHowDidYouFindUsAccess: boolean
  settingsTagsAccess: boolean
  settingsAcknowledgmentAccess: boolean
  createCandidateAccess: boolean
  processInterviews: {
    isAccess: boolean
    seeCandidateMessages: boolean
    editCandidateInformations: boolean
    seeActionsOnProcess: boolean
  }
}

export type ExportModule = {
  moduleId: 'EXPORT'
  isAccess: boolean
  canExportEvaluation: boolean
  canExportResource: boolean
  canExportCandidate: boolean
  canExportTimeSheet: boolean
}

export type ExpenseModule = {
  moduleId: 'EXPENSE'
  isAccess: boolean
  canCreateExpense: boolean
  canEditSettings: boolean
}

export type InvoiceModule = {
  moduleId: 'INVOICES'
  isAccess: boolean
  settingsAccess: boolean
  settingsBankAccountsAccess: boolean
  settingsInvoicingInformationAccess: boolean
  settingsInvoicingTermsAccess: boolean
  settingsMailTemplatesAccess: boolean
  settingsExpenseCategoriesAccess: boolean
  settingsEInvoicingAccess: boolean
}

export type SalesFunnelModule = {
  moduleId: 'SALESFUNNEL'
  isAccess: boolean
  showAll: boolean
  showClients: boolean
  showStats: boolean
}

export type AlertModule = {
  moduleId: 'ALERT'
  isAccess: boolean
  builderAccess: boolean
  monitorAccess: boolean
}

export type TicketModule = {
  isAccess: boolean;
  showAll: boolean;
}

export type AiAssistantModule = {
  isAccess: boolean
}

export type TrainingModule = {
  moduleId: 'TRAININGS'
  isAccess: boolean
  catalogAccess: boolean
  myTrainingsAccess: boolean
  createTrainingAccess: boolean
  statisticsAccess: boolean
  budgetAccess: boolean
  trainingAccess: boolean
  wishesAttributionAccess: boolean
  elearningCatalogAccess: boolean
  elearningBuilderAccess: boolean
  learningPathAccess: boolean
  elearningStatisticsAccess: boolean
  elearningAssignmentAccess: boolean
  canEditTraining: boolean
  canEditElearning: boolean
  canOrganizeSession: boolean
  canReopenPresenceSheet: boolean
  isAdmin: boolean
  settingsAccess: boolean
  settingsExternalTrainersAccess: boolean
  settingsLocationsAccess: boolean
  settingsCategoriesAccess: boolean
  settingsObjectItemCategoryAccess: boolean
  settingsCostCategoriesAccess: boolean
  settingsEvaluationFormsAccess: boolean
}

export type Modules = {
  alert: AlertModule;
  hr: HRModule
  dashboard: DashboardModule
  project: ProjectModule
  timesheet: TimesheetModule
  globalSettings: GlobalSettings
  leave: LeaveModule
  evaluation: EvaluationModule
  recruitment: RecruitmentModule
  export: ExportModule
  expense: ExpenseModule
  invoice: InvoiceModule
  salesFunnel: SalesFunnelModule
  ticket: TicketModule
  training: TrainingModule
  aiAssistant: AiAssistantModule
}

